import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import GroupIcon from "@mui/icons-material/Group";

import { State } from "../../../redux/slices";
import {
  CreateUsersStep,
  setCreateUsersStep,
} from "../../../redux/slices/users/createUsers";
import { useNavigate } from "react-router-dom";
import { fetchActionsNamesUsersConfig } from "../../../redux/actions/locations/locationsAsyncActions";
import {
  cardContentBaseSx,
  cardContentFlexSx,
  cardSx,
  chevronSx,
  contentBoxSx,
  dialogContentSx,
  dialogTitleSx,
  iconLeftSx,
  iconRightSx,
  iconSx,
  secondCardWrapperSx,
  subtitleSx,
  titleTypographySx,
} from "./ModalUserTypeSelection.styles";

interface ModalUserTypeSelectionProps {
  open: boolean;
  onClose: () => void;
  token: string;
}

const ModalUserTypeSelection: FC<ModalUserTypeSelectionProps> = ({
  open,
  onClose,
  token,
}) => {
  const [t] = useTranslation("global");
  const navigate = useNavigate();
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const { createUserSteps } = useSelector(
    (state: State) => state.CreateUsersReducer,
  );

  const [hoveredCard, setHoveredCard] = useState<"a" | "b" | null>(null);

  const prepareUsersValues = () => {
    navigate("/create-users");
    dispatch(fetchActionsNamesUsersConfig(token));
    dispatch(setCreateUsersStep({ ...createUserSteps, step: 1, option: "a" }));
  };

  const redirectToManagerUsers = () => {
    navigate("/create-admins");
  };

  const selectType = (option: "a" | "b") => {
    const next: CreateUsersStep = { ...createUserSteps, step: 1, option };
    dispatch(setCreateUsersStep(next));
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={dialogTitleSx}>{t("Users_add_users.type")}</DialogTitle>

      <DialogContent sx={dialogContentSx}>
        <Box
          onClick={prepareUsersValues}
          onMouseEnter={() => setHoveredCard("a")}
          onMouseLeave={() => setHoveredCard(null)}
        >
          <Card sx={cardSx("a", hoveredCard)}>
            <Box sx={iconLeftSx}>
              <GroupIcon sx={iconSx("a", hoveredCard)} />
            </Box>
            <Box sx={contentBoxSx}>
              <CardContent sx={cardContentBaseSx}>
                <Typography
                  component="div"
                  sx={titleTypographySx("a", hoveredCard)}
                >
                  {t("Users_add_users.site_user")}
                </Typography>
                <Typography variant="subtitle1" component="div" sx={subtitleSx}>
                  {t("Users_add_users.site_sub")}
                </Typography>
              </CardContent>
            </Box>
            <Box sx={iconRightSx}>
              <ChevronRightIcon sx={chevronSx("a", hoveredCard)} />
            </Box>
          </Card>
        </Box>

        <Box
          sx={secondCardWrapperSx}
          onClick={() => {
            selectType("b");
            redirectToManagerUsers();
          }}
          onMouseEnter={() => setHoveredCard("b")}
          onMouseLeave={() => setHoveredCard(null)}
        >
          <Card sx={cardSx("b", hoveredCard)}>
            <Box sx={iconLeftSx}>
              <AdminPanelSettingsIcon sx={iconSx("b", hoveredCard)} />
            </Box>
            <Box sx={contentBoxSx}>
              <CardContent sx={cardContentFlexSx}>
                <Typography
                  component="div"
                  sx={titleTypographySx("b", hoveredCard)}
                >
                  {t("Users_add_users.manager_user")}
                </Typography>
                <Typography variant="subtitle1" component="div" sx={subtitleSx}>
                  {t("Users_add_users.manager_sub")}
                </Typography>
              </CardContent>
            </Box>
            <Box sx={iconRightSx}>
              <ChevronRightIcon sx={chevronSx("b", hoveredCard)} />
            </Box>
          </Card>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ModalUserTypeSelection;
